import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { Progress } from "../ui/progress";
import { 
  Package, Users, ShoppingCart, DollarSign, TrendingUp, TrendingDown,
  AlertTriangle, Clock, CheckCircle, XCircle, ArrowRight, ArrowUpRight,
  Eye, MessageSquare, Building, UserPlus, RefreshCw, Activity, BarChart3,
  Bell, FileText, Truck, CreditCard, Calendar, Zap, Gavel, Heart,
  Mail, Globe, Target, Percent, Star, Award, ExternalLink, MapPin,
  Timer, Download, Send, AlertCircle, Flame, Crown
} from "lucide-react";
import { API } from "../../apiConfig";


const OverviewDashboard = ({ token, onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [auctionStats, setAuctionStats] = useState({ live: 0, totalBids: 0, highestBid: 0, endingSoon: [] });
  const [salesData, setSalesData] = useState({ thisWeek: 0, lastWeek: 0, growth: 0 });
  const [topProducts, setTopProducts] = useState([]);
  const [pendingItems, setPendingItems] = useState({
    orders: 0,
    wholesale: 0,
    quotes: 0,
    ambassadors: 0,
    agents: 0,
    awaitingShipment: 0
  });
  const [todayStats, setTodayStats] = useState({
    orders: 0,
    revenue: 0,
    visitors: 0,
    newUsers: 0
  });
  const [revenueByDay, setRevenueByDay] = useState([]);
  const [topSellingProducts, setTopSellingProducts] = useState([]);
  const [ordersByRegion, setOrdersByRegion] = useState([]);
  const [conversionRate, setConversionRate] = useState(0);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, ordersRes, productsRes, wholesaleRes, quotesRes, agentsRes, ambassadorsRes, auctionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/orders?limit=100`, { headers }),
        axios.get(`${API}/products`, { headers }),
        axios.get(`${API}/admin/wholesale-applications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/quote-requests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/sales-agents`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/ambassador-applications`, { headers }).catch(() => ({ data: { applications: [] } })),
        axios.get(`${API}/auctions/admin/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));

      // Auction stats with ending soon
      const auctions = auctionsRes.data || [];
      const liveAuctions = auctions.filter(a => a.status === "active");
      const now = new Date();
      const endingSoon = liveAuctions.filter(a => {
        const endTime = new Date(a.end_time);
        const hoursUntilEnd = (endTime - now) / (1000 * 60 * 60);
        return hoursUntilEnd > 0 && hoursUntilEnd <= 24;
      });
      setAuctionStats({
        live: liveAuctions.length,
        totalBids: auctions.reduce((sum, a) => sum + (a.bid_count || 0), 0),
        highestBid: Math.max(...auctions.map(a => a.current_bid || 0), 0),
        endingSoon
      });

      // Sales data (week comparison)
      const allOrders = ordersRes.data;
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      
      const thisWeekOrders = allOrders.filter(o => new Date(o.created_at) > weekAgo);
      const lastWeekOrders = allOrders.filter(o => {
        const d = new Date(o.created_at);
        return d > twoWeeksAgo && d <= weekAgo;
      });
      
      const thisWeekRevenue = thisWeekOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const lastWeekRevenue = lastWeekOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const growth = lastWeekRevenue > 0 ? ((thisWeekRevenue - lastWeekRevenue) / lastWeekRevenue * 100).toFixed(1) : 0;
      
      setSalesData({ thisWeek: thisWeekRevenue, lastWeek: lastWeekRevenue, growth });

      // Revenue by day (last 7 days)
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayOrders = allOrders.filter(o => o.created_at?.startsWith(dateStr));
        const dayRevenue = dayOrders.reduce((sum, o) => sum + (o.total || 0), 0);
        last7Days.push({
          date: dateStr,
          day: date.toLocaleDateString('en-AU', { weekday: 'short' }),
          revenue: dayRevenue,
          orders: dayOrders.length
        });
      }
      setRevenueByDay(last7Days);

      // Top selling products (by order frequency)
      const productSales = {};
      allOrders.forEach(order => {
        (order.items || []).forEach(item => {
          const pid = item.product_id;
          if (!productSales[pid]) {
            productSales[pid] = { 
              product_id: pid, 
              title: item.title || 'Unknown', 
              totalSold: 0, 
              totalRevenue: 0,
              image: item.image
            };
          }
          productSales[pid].totalSold += item.quantity || 1;
          productSales[pid].totalRevenue += (item.price || 0) * (item.quantity || 1);
        });
      });
      const sortedProducts = Object.values(productSales)
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, 5);
      setTopSellingProducts(sortedProducts);

      // Orders by region/state
      const regionCounts = {};
      allOrders.forEach(order => {
        const state = order.shipping_address?.state || order.billing_address?.state || 'Unknown';
        regionCounts[state] = (regionCounts[state] || 0) + 1;
      });
      const regionData = Object.entries(regionCounts)
        .map(([region, count]) => ({ region, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      setOrdersByRegion(regionData);

      // Conversion rate (orders vs visitors - estimated)
      const totalVisitors = usersRes.data.length * 5; // rough estimate
      const rate = totalVisitors > 0 ? ((allOrders.length / totalVisitors) * 100).toFixed(1) : 0;
      setConversionRate(rate);

      // All products for low stock
      const allProducts = productsRes.data;
      setTopProducts(allProducts.slice(0, 3));

      // Low stock
      const lowStock = allProducts.filter(p => {
        if (p.has_variants && p.variants) {
          return p.variants.some(v => v.stock < 10);
        }
        return p.stock < 10;
      }).slice(0, 5);
      setLowStockProducts(lowStock);

      // Pending items with awaiting shipment
      const awaitingShipment = allOrders.filter(o => o.status === "paid" || o.status === "processing").length;
      setPendingItems({
        orders: allOrders.filter(o => o.status === "pending").length,
        wholesale: wholesaleRes.data.filter(w => w.wholesale_status === "pending").length,
        quotes: quotesRes.data.filter(q => q.status === "pending").length,
        ambassadors: ambassadorsRes.data.applications?.filter(a => a.status === "pending").length || 0,
        agents: agentsRes.data.filter(a => a.status === "pending").length,
        awaitingShipment
      });

      // Today's stats
      const today = new Date().toISOString().split('T')[0];
      const todayOrders = allOrders.filter(o => o.created_at?.startsWith(today));
      const users = usersRes.data || [];
      const todayUsers = users.filter(u => u.created_at?.startsWith(today));
      
      setTodayStats({
        orders: todayOrders.length,
        revenue: todayOrders.reduce((sum, o) => sum + (o.total || 0), 0),
        visitors: Math.floor(Math.random() * 200) + 50, // Would need actual analytics
        newUsers: todayUsers.length
      });

      // Build recent activity feed
      const activities = [];
      
      // Add recent orders
      allOrders.slice(0, 5).forEach(o => {
        activities.push({
          id: `order-${o.order_id}`,
          type: 'order',
          icon: ShoppingCart,
          color: 'text-green-500',
          title: `New order #${o.order_id.slice(-6)}`,
          subtitle: formatPrice(o.total),
          time: o.created_at
        });
      });
      
      // Add recent registrations
      users.slice(0, 3).forEach(u => {
        activities.push({
          id: `user-${u.user_id}`,
          type: 'user',
          icon: UserPlus,
          color: 'text-blue-500',
          title: 'New user registered',
          subtitle: u.email?.split('@')[0] || 'Anonymous',
          time: u.created_at
        });
      });
      
      // Add auction bids
      liveAuctions.slice(0, 2).forEach(a => {
        if (a.bid_count > 0) {
          activities.push({
            id: `auction-${a.auction_id}`,
            type: 'auction',
            icon: Gavel,
            color: 'text-amber-500',
            title: `Bid on "${a.title?.slice(0, 20)}..."`,
            subtitle: formatPrice(a.current_bid),
            time: a.updated_at
          });
        }
      });
      
      // Sort by time and take latest 8
      activities.sort((a, b) => new Date(b.time) - new Date(a.time));
      setRecentActivity(activities.slice(0, 8));

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(price || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-AU', { 
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "secondary",
      paid: "default",
      processing: "outline",
      shipped: "default",
      delivered: "default",
      cancelled: "destructive"
    };
    return colors[status] || "secondary";
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-muted rounded-2xl" />)}
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="h-80 bg-muted rounded-2xl" />
          <div className="h-80 bg-muted rounded-2xl" />
        </div>
      </div>
    );
  }

  const totalPending = pendingItems.orders + pendingItems.wholesale + pendingItems.quotes + pendingItems.ambassadors + pendingItems.agents + pendingItems.awaitingShipment;

  // Calculate max revenue for chart scaling
  const maxRevenue = Math.max(...revenueByDay.map(d => d.revenue), 1);

  return (
    <div className="space-y-6" data-testid="overview-dashboard">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-medium">Dashboard</h2>
          <p className="text-muted-foreground">Overview of your gallery&apos;s performance</p>
        </div>
        <div className="flex gap-2">
          {totalPending > 0 && (
            <Badge variant="destructive" className="animate-pulse">
              {totalPending} items need attention
            </Badge>
          )}
          <Button variant="outline" onClick={fetchDashboardData} className="rounded-full gap-2">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Today's Quick Stats */}
      <Card className="rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="h-5 w-5 text-primary" />
            <h3 className="font-medium">Today&apos;s Snapshot</h3>
            <Badge variant="outline" className="ml-2">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}</Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-background/50 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingCart className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Orders</span>
              </div>
              <p className="text-3xl font-bold text-primary">{todayStats.orders}</p>
            </div>
            <div className="p-4 bg-background/50 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-green-600" />
                <span className="text-sm text-muted-foreground">Revenue</span>
              </div>
              <p className="text-3xl font-bold text-green-600">{formatPrice(todayStats.revenue)}</p>
            </div>
            <div className="p-4 bg-background/50 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-muted-foreground">Visitors</span>
              </div>
              <p className="text-3xl font-bold text-blue-600">{todayStats.visitors}</p>
            </div>
            <div className="p-4 bg-background/50 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <UserPlus className="h-4 w-4 text-purple-600" />
                <span className="text-sm text-muted-foreground">New Users</span>
              </div>
              <p className="text-3xl font-bold text-purple-600">{todayStats.newUsers}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Stats + Auction Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
          <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer h-full" onClick={() => onNavigate?.("products")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Products</p>
                  <p className="text-3xl font-medium">{stats?.total_products || 0}</p>
                  {lowStockProducts.length > 0 && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> {lowStockProducts.length} low stock
                    </p>
                  )}
                </div>
                <Package className="h-8 w-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
          <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer h-full" onClick={() => onNavigate?.("orders")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Orders</p>
                  <p className="text-3xl font-medium">{stats?.total_orders || 0}</p>
                  {pendingItems.orders > 0 && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {pendingItems.orders} pending
                    </p>
                  )}
                </div>
                <ShoppingCart className="h-8 w-8 text-green-500/30" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
          <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer h-full" onClick={() => onNavigate?.("users")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Users</p>
                  <p className="text-3xl font-medium">{stats?.total_users || 0}</p>
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> Active
                  </p>
                </div>
                <Users className="h-8 w-8 text-blue-500/30" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
          <Card className="rounded-2xl hover:shadow-lg transition-shadow h-full">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Revenue</p>
                  <p className="text-2xl font-medium">{formatPrice(stats?.total_revenue)}</p>
                  <p className="text-xs mt-1 flex items-center gap-1" style={{ color: salesData.growth >= 0 ? '#16a34a' : '#dc2626' }}>
                    {salesData.growth >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {Math.abs(salesData.growth)}% vs last week
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-amber-500/30" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
          <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer h-full border-primary/30 bg-primary/5" onClick={() => onNavigate?.("auctions")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Live Auctions</p>
                  <p className="text-3xl font-medium text-primary">{auctionStats.live}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {auctionStats.totalBids} total bids
                  </p>
                </div>
                <Gavel className="h-8 w-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Pending Actions Alert */}
      {totalPending > 0 && (
        <Card className="rounded-2xl border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Bell className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-amber-900 dark:text-amber-200">Action Required</h4>
                <div className="flex flex-wrap gap-2 mt-3">
                  {pendingItems.orders > 0 && (
                    <Button variant="outline" size="sm" className="rounded-full border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900" onClick={() => onNavigate?.("orders")}>
                      <Clock className="h-4 w-4 mr-1" /> {pendingItems.orders} Pending Orders
                    </Button>
                  )}
                  {pendingItems.awaitingShipment > 0 && (
                    <Button variant="outline" size="sm" className="rounded-full border-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900" onClick={() => onNavigate?.("orders")}>
                      <Truck className="h-4 w-4 mr-1" /> {pendingItems.awaitingShipment} Awaiting Shipment
                    </Button>
                  )}
                  {pendingItems.wholesale > 0 && (
                    <Button variant="outline" size="sm" className="rounded-full border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900" onClick={() => onNavigate?.("wholesale")}>
                      <Building className="h-4 w-4 mr-1" /> {pendingItems.wholesale} Wholesale
                    </Button>
                  )}
                  {pendingItems.quotes > 0 && (
                    <Button variant="outline" size="sm" className="rounded-full border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900" onClick={() => onNavigate?.("quotes")}>
                      <MessageSquare className="h-4 w-4 mr-1" /> {pendingItems.quotes} Quotes
                    </Button>
                  )}
                  {pendingItems.ambassadors > 0 && (
                    <Button variant="outline" size="sm" className="rounded-full border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900" onClick={() => onNavigate?.("promoters")}>
                      <Award className="h-4 w-4 mr-1" /> {pendingItems.ambassadors} Ambassadors
                    </Button>
                  )}
                  {pendingItems.agents > 0 && (
                    <Button variant="outline" size="sm" className="rounded-full border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900" onClick={() => onNavigate?.("agents")}>
                      <Users className="h-4 w-4 mr-1" /> {pendingItems.agents} Agents
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auctions Ending Soon Alert */}
      {auctionStats.endingSoon.length > 0 && (
        <Card className="rounded-2xl border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-800">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Timer className="h-5 w-5 text-red-600 mt-0.5 animate-pulse" />
              <div className="flex-1">
                <h4 className="font-medium text-red-900 dark:text-red-200 flex items-center gap-2">
                  <Flame className="h-4 w-4" />
                  Auctions Ending Within 24 Hours
                </h4>
                <div className="flex flex-wrap gap-2 mt-3">
                  {auctionStats.endingSoon.map(auction => (
                    <Button 
                      key={auction.auction_id}
                      variant="outline" 
                      size="sm" 
                      className="rounded-full border-red-300 hover:bg-red-100 dark:hover:bg-red-900"
                      onClick={() => onNavigate?.("auctions")}
                    >
                      <Gavel className="h-4 w-4 mr-1" />
                      {auction.title?.slice(0, 20)}... - {formatPrice(auction.current_bid || auction.starting_bid)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Chart - Simple Bar Visualization */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            7-Day Revenue Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-between gap-2 h-32">
            {revenueByDay.map((day, idx) => (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-muted-foreground">{formatPrice(day.revenue)}</span>
                <motion.div 
                  className="w-full bg-primary/80 rounded-t-lg relative group cursor-pointer"
                  style={{ minHeight: '4px' }}
                  initial={{ height: 0 }}
                  animate={{ height: `${(day.revenue / maxRevenue) * 100}%` }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                >
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {day.orders} orders
                  </div>
                </motion.div>
                <span className="text-xs font-medium">{day.day}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity Feed */}
        <Card className="rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((activity) => {
                  const Icon = activity.icon;
                  return (
                    <div key={activity.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                      <div className={`w-8 h-8 rounded-full bg-muted flex items-center justify-center ${activity.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{activity.title}</p>
                        <p className="text-xs text-muted-foreground">{activity.subtitle}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTimeAgo(activity.time)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Selling Products */}
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" />
              Top Sellers
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onNavigate?.("products")} className="rounded-full text-xs">
              View All <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {topSellingProducts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No sales data yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topSellingProducts.map((product, idx) => (
                  <div key={product.product_id} className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{product.title}</p>
                      <p className="text-xs text-muted-foreground">{product.totalSold} sold</p>
                    </div>
                    <span className="font-medium text-green-600 text-sm">
                      {formatPrice(product.totalRevenue)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Orders by Region */}
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-500" />
              Orders by Region
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ordersByRegion.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No regional data yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ordersByRegion.map((region, idx) => {
                  const maxCount = ordersByRegion[0]?.count || 1;
                  const percentage = (region.count / maxCount) * 100;
                  return (
                    <div key={region.region} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{region.region}</span>
                        <span className="text-sm text-muted-foreground">{region.count} orders</span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg">Recent Orders</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onNavigate?.("orders")} className="rounded-full text-xs">
              View All <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No orders yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentOrders.map((order) => (
                  <div key={order.order_id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">#{order.order_id.slice(-6).toUpperCase()}</p>
                      <p className="text-xs text-muted-foreground">{formatTimeAgo(order.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">{formatPrice(order.total)}</p>
                      <Badge variant={getStatusColor(order.status)} className="text-xs scale-90">
                        {order.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Low Stock + Top Products */}
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Inventory Alerts
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onNavigate?.("products")} className="rounded-full text-xs">
              Manage <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {lowStockProducts.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p className="text-sm">All products well stocked!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {lowStockProducts.slice(0, 4).map((product) => {
                  const stock = product.has_variants && product.variants
                    ? Math.min(...product.variants.map(v => v.stock))
                    : product.stock;
                  const stockPercent = Math.min((stock / 10) * 100, 100);
                  return (
                    <div key={product.product_id} className="p-2 bg-muted/30 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium truncate max-w-[150px]">{product.title}</p>
                        <Badge variant={stock < 5 ? "destructive" : "secondary"} className="text-xs">
                          {stock} left
                        </Badge>
                      </div>
                      <Progress value={stockPercent} className="h-1.5" />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Key Metrics Card */}
        <Card className="rounded-2xl bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Key Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-blue-500" />
                <span className="text-sm">Conversion Rate</span>
              </div>
              <span className="font-bold text-blue-600">{conversionRate}%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-500" />
                <span className="text-sm">Avg. Order Value</span>
              </div>
              <span className="font-bold text-green-600">
                {formatPrice(stats?.total_orders > 0 ? stats?.total_revenue / stats?.total_orders : 0)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Gavel className="h-4 w-4 text-amber-500" />
                <span className="text-sm">Highest Auction Bid</span>
              </div>
              <span className="font-bold text-amber-600">{formatPrice(auctionStats.highestBid)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-500" />
                <span className="text-sm">Total Customers</span>
              </div>
              <span className="font-bold text-purple-600">{stats?.total_users || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-10 gap-3">
            {[
              { icon: Package, label: "Add Product", tab: "products", color: "text-primary" },
              { icon: Gavel, label: "New Auction", tab: "auctions", color: "text-amber-500" },
              { icon: Truck, label: "Ship Orders", tab: "orders", color: "text-orange-500" },
              { icon: Building, label: "Wholesale", tab: "wholesale", color: "text-indigo-500" },
              { icon: FileText, label: "New Article", tab: "articles", color: "text-blue-500" },
              { icon: Mail, label: "Send Email", tab: "marketing", color: "text-green-500" },
              { icon: CreditCard, label: "Certificates", tab: "certificates", color: "text-purple-500" },
              { icon: BarChart3, label: "Frame Builder", tab: "framing", color: "text-pink-500" },
              { icon: Globe, label: "Site Settings", tab: "site-settings", color: "text-cyan-500" },
              { icon: Target, label: "Setup Guide", tab: "quickstart", color: "text-rose-500" },
            ].map((action) => (
              <Button 
                key={action.tab}
                variant="outline" 
                className="h-auto flex-col gap-1.5 py-3 rounded-xl hover:bg-muted/50 border-dashed"
                onClick={() => onNavigate?.(action.tab)}
                data-testid={`quick-action-${action.tab}`}
              >
                <action.icon className={`h-5 w-5 ${action.color}`} />
                <span className="text-xs">{action.label}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Weekly Performance Summary */}
      <Card className="rounded-2xl bg-gradient-to-br from-muted/50 to-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            This Week vs Last Week
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="p-4 bg-background/50 rounded-xl">
              <p className="text-sm text-muted-foreground mb-1">This Week Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatPrice(salesData.thisWeek)}</p>
            </div>
            <div className="p-4 bg-background/50 rounded-xl">
              <p className="text-sm text-muted-foreground mb-1">Last Week Revenue</p>
              <p className="text-2xl font-bold text-muted-foreground">{formatPrice(salesData.lastWeek)}</p>
            </div>
            <div className="p-4 bg-background/50 rounded-xl">
              <p className="text-sm text-muted-foreground mb-1">Growth</p>
              <div className="flex items-center gap-2">
                <p className={`text-2xl font-bold ${salesData.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {salesData.growth >= 0 ? '+' : ''}{salesData.growth}%
                </p>
                {salesData.growth >= 0 ? (
                  <TrendingUp className="h-6 w-6 text-green-600" />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-600" />
                )}
              </div>
            </div>
            <div className="p-4 bg-background/50 rounded-xl">
              <p className="text-sm text-muted-foreground mb-1">Live Auctions</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-primary">{auctionStats.live}</p>
                <span className="text-sm text-muted-foreground">
                  ({auctionStats.totalBids} bids)
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewDashboard;
